import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetServiceUsersQuery,
  useGetDashboardLocationsQuery,
  formatValue,
  computeLocationParams,
  type DashboardOptions
} from '@/entities/dashboard';
import { useGetOrganizationsQuery, type Organization } from '@/entities/organizations';

import { useUrlState, useProjectSiteParams } from '@/shared/lib';
import type { DateRangeValue, FilterGroup } from '@/shared/ui';

import { buildServiceUsersFilterChips } from './build-filter-chips';
import type { ServiceUsersUrlState, FormattedStats } from './types';

const DEFAULT_URL_STATE: ServiceUsersUrlState = {
  metric: 'W',
  period: 'L6M',
  fromDate: '',
  toDate: ''
};

export function useServiceUsers() {
  const { t } = useTranslation();
  const { projectId, siteId } = useProjectSiteParams();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([]);
  const [organizationsSearchTerm, setOrganizationsSearchTerm] = useState('');

  const { data: locationsData, isLoading: locationsLoading } = useGetDashboardLocationsQuery(
    {
      projectId
    },
    { skip: !projectId }
  );

  const { data: organizationsData, isLoading: organizationsLoading } = useGetOrganizationsQuery({
    search: organizationsSearchTerm,
    projectId
  });

  const organizationGroups: FilterGroup[] = useMemo(() => {
    if (!organizationsData?.organizations) return [];

    const groupedByType = new Map<string, Organization[]>();

    organizationsData.organizations.forEach((org) => {
      const typeDescription = org.orgType?.description || 'Other';
      if (!groupedByType.has(typeDescription)) {
        groupedByType.set(typeDescription, []);
      }
      groupedByType.get(typeDescription)!.push(org);
    });

    return Array.from(groupedByType.entries()).map(([typeDescription, orgs]) => ({
      group: typeDescription,
      groupId: typeDescription.toLowerCase().replace(/\s+/g, '-'),
      items: orgs.map((org) => ({
        id: org.id.toString(),
        name: `${org.name} (${org.abbreviation})`
      }))
    }));
  }, [organizationsData?.organizations]);

  const locations = useMemo(
    () =>
      locationsData?.locationGroups?.flatMap((group) => group.locations.map(({ id, name }) => ({ id, name }))) ?? [],
    [locationsData]
  );

  const allLocationIds = useMemo(() => locations.map((location) => location.id), [locations]);

  const organizations = useMemo(
    () => organizationGroups.flatMap((group) => group.items.map(({ id, name }) => ({ id, name }))),
    [organizationGroups]
  );

  const allOrganizationIds = useMemo(() => organizations.map((organization) => organization.id), [organizations]);

  const queryParams = useMemo(() => {
    const locationParams = computeLocationParams(locationsData?.locationGroups, selectedLocations);
    // Like Locations, every partner picked is no filter at all, not a list of every id
    const isOrganizationsSubset =
      selectedOrganizations.length > 0 && selectedOrganizations.length < allOrganizationIds.length;
    return {
      ...urlState,
      ...locationParams,
      projectId,
      siteId,
      partners: isOrganizationsSubset ? selectedOrganizations : undefined
    };
  }, [
    urlState,
    selectedLocations,
    locationsData?.locationGroups,
    projectId,
    siteId,
    selectedOrganizations,
    allOrganizationIds
  ]);

  const { data, isLoading, isFetching, error } = useGetServiceUsersQuery(queryParams);

  const updateOption = useCallback(
    (key: keyof DashboardOptions, value: string | string[]) => {
      setUrlState({ [key]: value });
    },
    [setUrlState]
  );

  const handleDateRangeChange = useCallback(
    (dateRange: DateRangeValue) => {
      setUrlState(dateRange as ServiceUsersUrlState);
    },
    [setUrlState]
  );

  const formattedStats: FormattedStats | null = useMemo(() => {
    if (!data) return null;

    return {
      totalReceived: formatValue(data.totalReceived, urlState.metric),
      totalReleased: formatValue(data.totalReleased, urlState.metric),
      servicePartners: data.serviceUsers.categories.length.toString()
    };
  }, [data, urlState.metric]);

  const resetLocations = useCallback(() => setSelectedLocations(allLocationIds), [allLocationIds]);

  const resetOrganizations = useCallback(() => setSelectedOrganizations(allOrganizationIds), [allOrganizationIds]);

  const removeFilter = useCallback(
    (key: string) => {
      if (key === 'date') {
        setUrlState({
          period: DEFAULT_URL_STATE.period,
          fromDate: DEFAULT_URL_STATE.fromDate,
          toDate: DEFAULT_URL_STATE.toDate
        });
        return;
      }

      setUrlState({ [key]: DEFAULT_URL_STATE[key as keyof ServiceUsersUrlState] });
    },
    [setUrlState]
  );

  const handleResetFilters = useCallback(() => {
    resetUrlState();
    resetLocations();
    resetOrganizations();
  }, [resetUrlState, resetLocations, resetOrganizations]);

  const activeFilters = useMemo(
    () =>
      buildServiceUsersFilterChips({
        state: urlState,
        defaults: DEFAULT_URL_STATE,
        t,
        locations,
        selectedLocations,
        organizations,
        selectedOrganizations,
        onRemove: removeFilter,
        onResetLocations: resetLocations,
        onResetOrganizations: resetOrganizations
      }),
    [
      urlState,
      t,
      locations,
      selectedLocations,
      organizations,
      selectedOrganizations,
      removeFilter,
      resetLocations,
      resetOrganizations
    ]
  );

  const activeFiltersCount = activeFilters.length;

  // Select all locations and organizations by default when their data first loads or project changes
  useEffect(() => {
    setSelectedLocations(allLocationIds);
  }, [allLocationIds, projectId]);

  useEffect(() => {
    setSelectedOrganizations(allOrganizationIds);
  }, [allOrganizationIds, projectId]);

  return {
    // Data
    data,
    formattedStats,
    locationsData,
    organizationGroups,

    // Loading states
    isLoading,
    isFetching,
    locationsLoading,
    organizationsLoading,
    error,

    // Filters
    urlState,
    selectedLocations,
    selectedOrganizations,
    organizationsSearchTerm,
    activeFilters,
    activeFiltersCount,
    hasActiveFilters: activeFiltersCount > 0,

    // Handlers
    updateOption,
    handleDateRangeChange,
    setSelectedLocations,
    setSelectedOrganizations,
    setOrganizationsSearchTerm,
    removeFilter,
    resetFilters: handleResetFilters
  };
}
